// Utilities
import { defineStore } from 'pinia';
import httpApi from '@/api';
import Swal from 'sweetalert2';
import { FormDataInputs, SelectItem } from '@/types';
import { RouteLocationNormalized } from 'vue-router';
import { apiErrorText, apiErrorFooter } from '@/helpers/ApiErrorText';
import { isApiError } from '@/helpers/ApiError';
import { MAX_ENTRADAS, capSeleccion } from '@/helpers/Limits';
import { getCaptchaToken } from '@/composables/useRecaptcha';
import { i18n } from '@/plugins/vuetify';

export interface VerifyResponse {
  message: string;
  success: boolean;
  /** Solo llega cuando success es false: autoriza adjuntar el CV. */
  ticket?: string;
}

/** Margen de 5 minutos sobre los 30 del backend, para no enviar un ticket muerto. */
const TICKET_TTL_MS = 25 * 60 * 1000;

/**
 * Con sitekey configurada el token pasa a ser obligatorio: el backend
 * rechazara toda peticion que no lo lleve. Sin ella el captcha queda inactivo
 * y las peticiones salen sin token, como en desarrollo local.
 */
const CAPTCHA_ACTIVO = !!import.meta.env.VITE_RECAPTCHA_SITEKEY;

const CAPTCHA_ERROR = 'captcha-no-disponible';

/** El aviso ya se mostró al obtener el token; no debe alertarse dos veces. */
const esErrorDeCaptcha = (error: unknown): boolean => (
  error instanceof Error && error.message === CAPTCHA_ERROR
);

interface TipoID { text: string, value: string }

type FechaMode = 'year' | 'month' | 'months';

type ItemsValues = { text?: string, value?: string | number };

export const useAppStore = defineStore('app', {
  state: () => ({
    locale: 'es',
    onlyCV: false,
    loading: false,
    step: 1,
    ticket: null as string | null,
    ticketIssuedAt: null as number | null,
    verifyMessage: '',
    verifyData: {
      vorna: '',
      nachn: '',
      icnum: '',
      gbdat: '',
      email: '',
      appName: '',
    },
    candidatoVerify: false,
    formDataInputs: <FormDataInputs>[],
    initialState: {
      tipoID: null as TipoID | null,
      identificacion: '',
      identificacionMSK: '',
      identificationActive: true,
      nombre: '',
      primerApellido: '',
      segundoApellido: '',
      estatusResidencia: null,
      fechaVigencia: null as Date | null,
      fechaVigencia_format: null as string | null,
      fechaVigecia_month: -200,
      menuVigencia: false,
      tieneCorreo: true,
      email: '',
      fechaNacimiento: null as Date | null,
      fechaNacimiento_format: null as string | null,
      fechaNacimiento_month: 0,
      menu: false,
      menuLicencia: false,
      fechaMode: <FechaMode>'year',
      tratamiento: null,
      celular: null,
      estadoCivil: null,
      cantidadHijos: null,
      nacionalidad: null as ItemsValues | null,
      genero: null,
      paisNacimiento: null,
      lugarNacimiento: null as ItemsValues | null,
      otroLugarNacimiento: null,
      gpcTrabajado: false,
      licencia: false,
      categoriaLicencia: null,
      fechaVigenciaLicencia: null as Date | null,
      fechaVigenciaLicencia_format: null as string | null,
      rotativo: false,
      movilidad: false,
      comoLlegaste: null as ItemsValues | null,
      comoLlegasteOtro: null,
      tieneFamiliar: false,
      familiarNombre: null,
      pasantias: null as ItemsValues[] | null,
      datosPasantia: {
        carrera: null as ItemsValues | null,
        otraCarrera: '',
        universidad: null as ItemsValues | null,
        otraUniversidad: '',
        calificacion: null,
        documentos: {
          cartaUniversidad: {
            extension: '',
            archivo: null as string | null,
          },
          curriculum: {
            extension: '',
            archivo: null as string | null,
          },
          recordNotas: {
            extension: '',
            archivo: null as string | null,
          },
          motivacion: {
            extension: '',
            archivo: null as string | null,
          },
        },
      },
      referido: false,
      referidoNombre: null,
      interes: null,
      vacantes: null as ItemsValues[] | null,
      archivoCV: null as string | null,
      direccion: {
        pais: null,
        ciudad: '',
        otra_provincia: '',
        provincia: null as ItemsValues | null,
        sector: '',
        calle: '',
        numero: '',
        tel_residencial: null,
      },
      educacion: [
        {
          tipoEducacion: null as ItemsValues | null,
          otroTipoEducacion: '',
          mesInicio: null as ItemsValues | null,
          fechaInicio: null,
          titulo: null as ItemsValues | null,
          otroTitulo: '',
          entidadEducativa: null as ItemsValues | null,
          entidadOptions: [],
          OtraEntidadEducativa: '',
          pais: null,
          estatus: null as ItemsValues | null,
          mesFin: null as ItemsValues | null,
          fechaFin: null,
        },
      ],
      idiomas: [
        {
          idioma: null as ItemsValues | null,
          nivel: null as ItemsValues | null,
        },
      ],
      expLaboral: true,
      experienciaLaboral: [
        {
          empresa: null,
          mesInicio: null as ItemsValues | null,
          fechaInicio: null,
          telefono: null,
          pais: null,
          provincia: null as ItemsValues | null,
          provinciaItems: [] as SelectItem[],
          otraProvincia: '',
          posicion: null as ItemsValues | null,
          otraPosicion: '',
          sectorEmpresarial: null as ItemsValues | null,
          otroSectorEmpresarial: '',
          activo: false,
          razonSalida: null as ItemsValues | null,
          mesSalida: null as ItemsValues | null,
          fechaSalida: null,
          rangoSalario: null,
          supervisor: null,
        },
      ],
    },
  }),
  getters: {
    getLocale: (state) => state.locale,
    getformDataInputs: (state) => state.formDataInputs,
    isTicketValid: (state) => !!state.ticket
      && !!state.ticketIssuedAt
      && Date.now() - state.ticketIssuedAt < TICKET_TTL_MS,
  },
  actions: {
    setLocale(current: string): string {
      this.locale = current === 'es' ? 'en' : 'es';
      return this.locale;
    },
    /**
     * Obtiene el token de captcha exigido por el backend.
     *
     * Si el captcha esta configurado y el token no se consigue, aborta con un
     * mensaje: el backend rechazara la peticion, y es preferible decirle al
     * candidato que recargue antes que dejarlo interpretar un 403.
     *
     * @param action Nombre de la accion para el scoring de Google.
     * @throws Error si el captcha esta activo y no hay token.
     */
    async requireCaptchaToken(action: string): Promise<string | null> {
      const token = await getCaptchaToken(action);
      if (!token && CAPTCHA_ACTIVO) {
        Swal.fire({
          text: i18n.global.t('$vuetify.errores.captchaNoDisponible'),
          icon: 'error',
        });
        throw new Error(CAPTCHA_ERROR);
      }
      return token;
    },
    /**
     * Guarda uno de los documentos de pasantia ya convertido a base64.
     *
     * @param tipoDocumento Clave del documento en datosPasantia.documentos.
     * @param base64 Contenido del archivo; cadena vacia para limpiarlo.
     * @param extension Extension en minusculas; solo pdf y docx se aceptan.
     */
    setDocumentos(tipoDocumento: string, base64: string, extension: string) {
      const { documentos } = this.initialState.datosPasantia;
      const documento = documentos[tipoDocumento as keyof typeof documentos];
      if (!documento) return;

      const prefijos: Record<string, string> = {
        pdf: 'data:application/pdf;base64,',
        docx: 'data:application/vnd.openxmlformats-officedocument.wordprocessingml.document;base64,',
      };

      documento.extension = extension;
      // Una extension no admitida limpia el archivo: dejar el anterior lo
      // enviaria aunque el formulario haya rechazado la seleccion nueva.
      documento.archivo = prefijos[extension]
        ? base64.replace(prefijos[extension], '')
        : null;
    },
    setArchivoCV(base64: string) {
      this.initialState.archivoCV = base64.replace('data:application/pdf;base64,', '');
    },
    /**
     * Envía el documento una sola vez con el ticket vigente.
     *
     * No captura errores: los propaga para que el orquestador decida entre
     * renovar el ticket o informar al usuario.
     *
     * @param base64 Contenido del PDF, con o sin prefijo data-URI.
     * @param route Ruta actual, de la que se toma el paramID de feria.
     */
    async postDocument(base64: string, route: RouteLocationNormalized) {
      const appName = String(route?.params?.paramID ?? import.meta.env.VITE_APP_NAME);

      const data = {
        tipoId: this.initialState.tipoID,
        identificacion: this.initialState.identificacion,
        email: this.initialState.email,
        ticket: this.ticket,
        document: base64.replace('data:application/pdf;base64,', ''),
        appName,
        // Se pide justo antes del envio: el token caduca en unos dos minutos.
        captchaToken: await this.requireCaptchaToken('document'),
      };

      const api = await httpApi();
      const resp = await api.post('api/candidato/document', data);
      const result = resp.data;

      // El ticket es de un solo uso: dejarlo vivo invitaría a un doble envío.
      this.ticket = null;
      this.ticketIssuedAt = null;

      if (result.success && result.message?.length > 0) {
        Swal.fire({
          text: result.message,
          icon: 'info',
        });
      }

      return true;
    },
    /**
     * Adjunta el CV de un candidato ya existente.
     *
     * El ticket que autoriza el envío es de un solo uso y caduca, así que si
     * está ausente o el backend lo rechaza se renueva y se reintenta una vez.
     * Toda la recuperación ocurre bajo el mismo `loading`, de modo que el
     * usuario solo percibe un envío.
     *
     * @param base64 Contenido del PDF.
     * @param route Ruta actual.
     * @returns true si el documento quedó registrado.
     */
    async uploadOnlyDocument(base64: string, route: RouteLocationNormalized) {
      this.loading = true;
      // Local a la invocación: como estado del store quedaría persistido y
      // podría inhabilitar la recuperación de forma permanente.
      let renewed = false;

      const renewTicket = async (): Promise<boolean> => {
        if (renewed) return false;
        renewed = true;
        const data = await this.runVerify(route);
        // success true significa que ya no es un candidato existente: no hay
        // ticket posible y el flujo de solo-CV dejó de aplicar.
        return !!data && !data.success && this.isTicketValid;
      };

      try {
        // Sin ticket en memoria (por ejemplo tras recargar la página) se
        // renueva de entrada, evitando un envío condenado a fallar.
        if (!this.isTicketValid && !await renewTicket()) {
          Swal.fire({
            text: this.verifyMessage || apiErrorText(null),
            icon: 'error',
          });
          return false;
        }

        try {
          return await this.postDocument(base64, route);
        } catch (error: any) {
          if (!isApiError(error) || error.code !== 'TICKET_EXPIRED') throw error;
          if (!await renewTicket()) throw error;
          return await this.postDocument(base64, route);
        }
      } catch (error: any) {
        if (!esErrorDeCaptcha(error)) {
          Swal.fire({
            text: apiErrorText(error),
            footer: apiErrorFooter(error),
            icon: 'error',
          });
        }
        return false;
      } finally {
        this.loading = false;
      }
    },
    /**
     * Ejecuta el POST de verificación y guarda el ticket resultante.
     *
     * Deliberadamente sin efectos de interfaz: no muestra alertas, no cambia
     * flags de navegación ni toca `loading`. Así puede reutilizarse para
     * renovar un ticket expirado sin que el usuario perciba nada.
     *
     * @param route Ruta actual, de la que se toma el paramID de feria.
     * @param validation true en el flujo de pasantía.
     * @returns La respuesta del backend, o null si la petición falló.
     */
    async runVerify(
      route: RouteLocationNormalized,
      validation: boolean = false,
    ): Promise<VerifyResponse | null> {
      const paramID = validation
        ? `${import.meta.env.VITE_APP_NAME}_PASANTIA`
        : String(route?.params?.paramID ?? import.meta.env.VITE_APP_NAME);

      const fechaNacimiento = this.initialState.fechaNacimiento || '';
      this.verifyData.vorna = window.btoa(this.initialState.nombre || '');
      this.verifyData.nachn = window.btoa(this.initialState.primerApellido || '');
      this.verifyData.icnum = window.btoa(this.initialState.identificacion || '');
      this.verifyData.email = window.btoa(this.initialState.email || '');
      this.verifyData.gbdat = window.btoa(fechaNacimiento.toString().slice(0, 10).replace(/\D/g, ''));
      this.verifyData.appName = paramID;

      const api = await httpApi();
      const resp = await api.post('api/candidato/verify', {
        ...this.verifyData,
        captchaToken: await this.requireCaptchaToken('verify'),
      });
      const data: VerifyResponse = resp.data ?? {};

      this.verifyMessage = data.message ?? '';
      this.ticket = typeof data.ticket === 'string' ? data.ticket : null;
      this.ticketIssuedAt = this.ticket ? Date.now() : null;

      return data;
    },
    async verifyCandidato(route: RouteLocationNormalized, validation: boolean = false) {
      this.loading = true;
      try {
        const data = await this.runVerify(route, validation);
        if (!data) return;

        if (!data.success && (data.message?.length ?? 0) > 0 && !validation) {
          Swal.fire({
            text: data.message,
            icon: 'info',
          });
          // Activar solo subir CV
          this.onlyCV = true;
          return;
        }

        this.candidatoVerify = true;
      } catch (error: any) {
        if (!esErrorDeCaptcha(error)) {
          Swal.fire({
            text: apiErrorText(error),
            footer: apiErrorFooter(error),
            icon: 'error',
          });
        }
      } finally {
        this.loading = false;
      }
    },
    async getDataForm(lang: string, route: RouteLocationNormalized) {
      this.loading = true;
      try {
        const paramID = String(route?.params?.paramID ?? import.meta.env.VITE_APP_NAME);
        const appName = paramID;
        const api = await httpApi();
        const resp = await api.get(`api/formadmission?lang=${lang}&appName=${appName}`);
        this.formDataInputs = resp.data;

        // Se compara como texto: el backend envía el string 'false', pero
        // tolerar el boolean evita que la feria quede abierta si eso cambia.
        if (String(resp.data?.FERIA_ACTIVA).toLowerCase() === 'false') {
          window.location.href = '/';
        }
      } catch (error: any) {
        // Sin catálogos el formulario es inservible, así que un fallo de
        // infraestructura sí se informa. Un 4xx se mantiene silencioso.
        if (isApiError(error) && (error.status >= 500 || error.status === 0)) {
          Swal.fire({
            text: apiErrorText(error),
            footer: apiErrorFooter(error),
            icon: 'error',
          });
        }
      } finally {
        this.loading = false;
        setTimeout(() => {
        }, 8000);
      }
    },
    /**
     * Catalogo de provincias de un pais. Devuelve siempre un arreglo: Vuetify
     * revienta en transformItems si `items` llega nulo, y esta cascada falla
     * con solo perder la red. Ver CANDIDATOS-GPC-CF.
     */
    async getProvinciaPais(pais: string): Promise<SelectItem[]> {
      this.loading = true;
      try {
        const appName = import.meta.env.VITE_APP_NAME;
        const api = await httpApi();
        const resp = await api.get(`api/formadmission?lang=${this.locale}&land=${pais}&appName=${appName}`);
        const { PROVINCIA } = resp.data;

        const arrCiudad: SelectItem[] = (PROVINCIA ?? []).map(
          (v: any) => ({ text: v.BEZEI.toUpperCase(), value: v.BLAND }),
        );

        arrCiudad.sort((a: any, b: any) => {
          const nameA = a.text; // ignore upper and lowercase
          const nameB = b.text; // ignore upper and lowercase
          if (nameA < nameB) {
            return -1;
          }
          if (nameA > nameB) {
            return 1;
          }

          // names must be equal
          return 0;
        });

        return arrCiudad;
      } catch (error: any) {
        // Cascada opcional: no se alerta al usuario. El interceptor ya reporta a Sentry.
        return [];
      } finally {
        this.loading = false;
      }
    },
    addEducacion() {
      if (this.initialState.educacion.length < MAX_ENTRADAS) {
        this.initialState.educacion.push({
          tipoEducacion: null as ItemsValues | null,
          otroTipoEducacion: '',
          mesInicio: null as ItemsValues | null,
          fechaInicio: null,
          titulo: null as ItemsValues | null,
          otroTitulo: '',
          entidadEducativa: null as ItemsValues | null,
          entidadOptions: [],
          OtraEntidadEducativa: '',
          pais: null,
          estatus: null as ItemsValues | null,
          mesFin: null as ItemsValues | null,
          fechaFin: null,
        });
      }
    },
    addIdioma() {
      if (this.initialState.idiomas.length < MAX_ENTRADAS) {
        this.initialState.idiomas.push({
          idioma: null as ItemsValues | null,
          nivel: null as ItemsValues | null,
        });
      }
    },
    addExpLaboral() {
      if (this.initialState.experienciaLaboral.length < MAX_ENTRADAS) {
        this.initialState.experienciaLaboral.push({
          empresa: null,
          mesInicio: null as ItemsValues | null,
          fechaInicio: null,
          telefono: null,
          pais: null,
          provincia: null as ItemsValues | null,
          provinciaItems: [] as SelectItem[],
          otraProvincia: '',
          posicion: null as ItemsValues | null,
          otraPosicion: '',
          sectorEmpresarial: null as ItemsValues | null,
          otroSectorEmpresarial: '',
          activo: false,
          razonSalida: null as ItemsValues | null,
          mesSalida: null as ItemsValues | null,
          fechaSalida: null,
          rangoSalario: null,
          supervisor: null,
        });
      }
    },
    delExpLaboral(idx: number) {
      if (this.initialState.experienciaLaboral.length > 1) {
        this.initialState.experienciaLaboral.splice(idx, 1);
      }
    },
    delEducacion(idx: number) {
      if (this.initialState.educacion.length > 1) {
        this.initialState.educacion.splice(idx, 1);
      }
    },
    delIdioma(idx: number) {
      if (this.initialState.idiomas.length > 1) {
        this.initialState.idiomas.splice(idx, 1);
      }
    },
    async postCandidato(id: string) {
      this.loading = true;
      const paramID = String(
        id
        ?? (this.initialState.pasantias
        && this.initialState.pasantias?.length > 0)
          ? `${import.meta.env.VITE_APP_NAME}_PASANTIA` : import.meta.env.VITE_APP_NAME,
      );
      if (this.initialState.archivoCV
        || this.initialState.pasantias) {
        const {
          tipoID,
          identificacion,
          estatusResidencia,
          fechaVigencia,
          nombre,
          primerApellido,
          segundoApellido,
          celular,
          email,
          tratamiento,
          fechaNacimiento,
          genero,
          paisNacimiento,
          lugarNacimiento,
          gpcTrabajado,
          movilidad,
          rotativo,
          referido,
          referidoNombre,
          tieneCorreo,
          comoLlegaste,
          comoLlegasteOtro,
          nacionalidad,
          interes,
          categoriaLicencia,
          fechaVigenciaLicencia,
          cantidadHijos,
          vacantes,
          direccion,
          estadoCivil,
          otroLugarNacimiento,
          familiarNombre,
          educacion,
          idiomas,
          expLaboral,
          archivoCV,
          pasantias,
          datosPasantia,
        } = this.initialState;

        const data = {
          infoCandidato: {
            appName: paramID,
            tipoId: tipoID,
            identificacion,
            estatus_residencia: estatusResidencia,
            fecha_residencia: fechaVigencia,
            nombre,
            primer_apellido: primerApellido,
            segundo_apellido: segundoApellido,
            celular,
            email,
            tratamiento,
            fecha_nacimiento: fechaNacimiento,
            genero,
            pais_nacimiento: paisNacimiento,
            lugar_nacimiento: lugarNacimiento,
            gpcTrabajado,
            movilidad,
            rotativo,
            referido,
            referido_nom: referidoNombre,
            tiene_correo: tieneCorreo,
            llegaste: comoLlegaste,
            llegaste_otro: comoLlegasteOtro,
            nacionalidad,
            interes,
            categoriaLicencia,
            vigenciaLicencia: fechaVigenciaLicencia,
            hijos: cantidadHijos,
            // Los selectores multiples no limitan la seleccion, pero el
            // backend rechaza mas de MAX_ENTRADAS. Se recorta aqui para que el
            // rechazo no ocurra despues de haber subido el CV.
            vacantes: capSeleccion(vacantes),
            direccion,
            estado_civil: estadoCivil,
            otroLugarNacimiento,
            familiar_nom: familiarNombre,
            educacion,
            idiomas,
            ExpLaboral: expLaboral,
            document: archivoCV,
            pasantias: capSeleccion(pasantias),
            datosPasantia,
          },
        };

        try {
          const api = await httpApi();
          // Despues de armar el payload: serializar megas de base64 consumiria
          // buena parte de los dos minutos que dura el token.
          const captchaToken = await this.requireCaptchaToken('create');
          const resp = await api.post('api/candidato/create', { ...data, captchaToken });
          if (resp.data.success) {
            Swal.fire({
              text: resp.data.message,
              icon: 'success',
            }).then(() => {
              this.$reset();
              window.location.href = '/';
            });
          } else {
            Swal.fire({
              title: resp.data.message,
              icon: 'error',
            });
          }
        } catch (error: any) {
          // Se informa aquí y no se relanza: el llamador tenía un catch vacío,
          // así que un fallo de envío no llegaba a mostrarse. Los datos del
          // formulario se conservan para que el candidato pueda reintentar.
          if (!esErrorDeCaptcha(error)) {
            Swal.fire({
              text: apiErrorText(error),
              footer: apiErrorFooter(error),
              icon: 'error',
            });
          }
        } finally {
          this.loading = false;
          setTimeout(() => {
          }, 8000);
        }
      } else {
        const msg = 'Error en el archivo CV, volver intentar.';
        // eslint-disable-next-line no-console
        console.info(msg);
      }
    },
  },
  // Lista explícita: el ticket caduca y no debe revivir tras una recarga, y
  // persistir `loading` dejaría el overlay pegado si se cierra la pestaña a
  // media petición. Lo que sí sobrevive es el formulario a medio llenar.
  persist: {
    paths: ['locale', 'step', 'onlyCV', 'candidatoVerify', 'formDataInputs', 'initialState'],
  },
});

// export default useAppStore;
